/**
 * Toucan Reads — the shell. It draws the header and the hash-routed page
 * below it, and handles the few things that belong to no page, such as
 * Escape, the brand menu and the sync badge. Each page handles everything
 * else about itself.
 */

import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import {
  glob,
  isStandaloneApp,
  listById,
  required,
  restoreScroll,
  ToucanAppMenu,
  ToucanHeaderAction,
  ToucanPageHeading,
  ToucanSettings,
  useDoneKey,
  useRoute,
  useStore,
} from "toucan";
import { SaveFromBrowser } from "./components/SaveFromBrowser.tsx";
import { mergedInto } from "./enrich.ts";
import { AddPage, FORM_ID as ADD_FORM } from "./pages/AddPage.tsx";
import {
  ImportPage,
  FORM_ID as IMPORT_FORM,
  importForm,
} from "./pages/ImportPage.tsx";
import { EditPage } from "./pages/EditPage.tsx";
import { LibraryPage } from "./pages/LibraryPage.tsx";
import { ListsPage } from "./pages/ListsPage.tsx";
import { ReadingAction, ReadPage } from "./pages/ReadPage.tsx";
import { itemById } from "./model.ts";
import { beingPlaced } from "./place.ts";
import {
  rememberList,
  rememberReading,
  rememberSort,
  storedSort,
} from "./remembered.ts";
import {
  currentRoute,
  go,
  isLibraryRoute,
  leavePath,
  libraryPath,
  noteLibraryRoute,
  routeItemId,
  routeListId,
} from "./routes.ts";
import { app, store } from "./state.ts";
import { pageTitle } from "./title.ts";
import { usePrecache } from "./precache.ts";
import { useSweep } from "./sweep.ts";
import type { JSX } from "preact";
import type { Item, ItemSort } from "./model.ts";
import type { Route } from "./routes.ts";

/* While reading, the header slides off the top as the reading scrolls down,
   and comes back for a scroll up, a pointer near the top edge or the end of
   the article. Everywhere else it stays put. */
glob(`
  /* While reading, the bar is an overlay rather than a row of the column, so
     its slide never moves the text. Its opaque background and shadow cover
     the words passing beneath it. */
  body > header[data-floating="true"] {
    position: absolute;
    inset: 0 0 auto 0;
    background: var(--toucan-surface);
    box-shadow: var(--toucan-shadow-sm);
  }

  /* Top padding the height of the bar, so the opening screen is not under
     it. The padding scrolls away with the article, and further down the words
     run under the bar. The shell sets the height from the bar. */
  body > main[data-under-bar="true"] {
    padding-top: var(--toucan-bar-height, 0px);
  }

  body > header {
    transition: transform 0.25s ease;
  }

  /* On iOS the bar has no colour of its own, so the reading slides under it
     and the scroll edge fade (theme.css) covers it, without a shadow. */
  :root[data-platform="ios"] body > header[data-floating="true"] {
    box-shadow: none;
  }

  body > header[data-retracted="true"] {
    transform: translateY(-100%);
  }

  /* An article's title is long, so the reading bar sets it at the size iOS
     gives every page's title, where Material's would cut it after a word or
     two. */
  body > header[data-floating="true"] .page-title {
    font-size: 1.0625rem;
  }
`);

/** How close to the top edge a pointer has to come to call the bar back. */
const NEAR_TOP = 48;

/** How far into the pane, in pixels, counts as having left the top of it. */
const PAST_TOP = 80;

/** How far in, in pixels, before the bar shows the article's title. Larger
    than `PAST_TOP`, so the title doesn't flash up while the bar slides away. */
const TITLE_PAST_TOP = 240;

/** How far, in pixels, the reading has to move against the way it was going
    before that counts as a change of direction, either way round. A scroll
    on a hand-held phone arrives as events whose sign flips, and without this
    each flip would bring the bar back over the text. */
const SHAKE = 24;

/** The routes that are sub-pages: everything but the library's own three. */
type SubPage = "edit" | "read" | "add" | "import" | "lists" | "settings";

/** What the header names each sub-page. The library has the brand row.
    A read screen's heading is the article itself, decided per render. */
const TITLES: Record<Exclude<SubPage, "read">, string> = {
  edit: "Edit",
  add: "Save a link",
  import: "Import articles",
  lists: "Manage lists",
  settings: "Settings",
};

/** Which form the header's Save submits, for the pages that have one. */
const SAVES: Partial<Record<Route["name"], string>> = {
  add: ADD_FORM,
  import: IMPORT_FORM,
};

/** The brand row, with the app's icon, its name and the dropdown menu. */
function BrandHeader() {
  return (
    <ToucanAppMenu app={app()} label="Reads" icon="icon.svg">
      <li>
        <button
          type="button"
          onClick={() => {
            go("lists");
          }}
        >
          Manage lists
        </button>
      </li>
      <li>
        <button
          type="button"
          onClick={() => {
            go("import");
          }}
        >
          Import articles
        </button>
      </li>
    </ToucanAppMenu>
  );
}

/** The slim sub-page bar, with back, the page's name and its one action. */
function PageHeader({
  route,
  item,
  atTop,
  retracted,
}: {
  route: Extract<Route, { name: SubPage }>;
  item: Item | undefined;
  atTop: boolean;
  retracted: boolean;
}) {
  // The import's form leaves the screen once the paste is in, and a Save
  // with nothing to press is worse than none.
  const [importing, setImporting] = useState(true);
  useEffect(() => importForm.subscribe(setImporting), []);
  const save =
    route.name === "import" && !importing ? undefined : SAVES[route.name];
  // A read screen's bar shows the article's title only once scrolled past
  // `TITLE_PAST_TOP`, above which the page's own title is on screen, and not
  // while the bar is retracted, so a fast scroll doesn't flash it mid-slide.
  const heading =
    route.name === "read"
      ? atTop || retracted
        ? ""
        : required(item, "item").title
      : TITLES[route.name];
  return (
    <>
      <ToucanPageHeading
        onBack={() => {
          go(leavePath());
        }}
      >
        {heading}
      </ToucanPageHeading>
      {save !== undefined && (
        <ToucanHeaderAction variant="save" form={save}>
          Save
        </ToucanHeaderAction>
      )}
      {/* The reading screen's link to the original, `ReadingAction`. */}
      {route.name === "read" && item !== undefined && (
        <ReadingAction item={item} />
      )}
    </>
  );
}

/** The route to show, which is the library while a stale one redirects. */
function shownRoute(route: Route, stale: boolean): Route {
  return stale ? { name: "library" } : route;
}

export function Shell() {
  const route = useRoute(currentRoute);
  const [sort, setSort] = useState<ItemSort>(storedSort);
  // The library's search is kept up here, since the library unmounts while an
  // article is read and the search should survive that. It is not remembered
  // past the visit (see `remembered.ts`).
  const [query, setQuery] = useState("");
  useStore(app());

  const named = routeItemId(route);
  const item: Item | undefined =
    named === undefined ? undefined : itemById(store(), named);
  // An item page whose item has gone (deleted here or on another device)
  // redirects to the item it was merged into. Failing that, it goes back to
  // the library view it was opened from. The edit page's delete has already set
  // that same hash, so any other target would overrule it.
  const stale = named !== undefined && item === undefined;
  const wentTo = named === undefined ? undefined : mergedInto(named);
  useEffect(() => {
    if (stale) {
      go(wentTo === undefined ? libraryPath() : `edit/${wentTo}`);
    }
  }, [stale, wentTo]);
  // A list that has gone (a stale link, or deleted on another device)
  // redirects to the whole library's address.
  const staleList =
    route.name === "list" && listById(store().lists, route.id) === undefined;
  useEffect(() => {
    if (staleList) {
      go("");
    }
  }, [staleList]);
  const shown = shownRoute(route, stale || staleList);
  // Record the route itself, not the library stand-in `shown` uses while a
  // deleted item's page redirects, which would forget the list. A deleted
  // list is not recorded.
  if (!staleList) {
    noteLibraryRoute(route);
  }
  // Recorded for a share sheet that relaunches the app over the reader. That
  // reload doesn't run any cleanup, so the next route clears it, not the reader
  // unmounting.
  const readingId = route.name === "read" ? route.id : undefined;
  useEffect(() => {
    rememberReading(readingId);
  }, [readingId]);
  // Look up the links nobody has yet, such as the share you swiped away from
  // or the tail of a bulk paste, whenever the library is on screen.
  useSweep(isLibraryRoute(shown));
  // And fetch the items' content so they can be read offline. `held` is the
  // ids of the items on this device, which the cards show.
  const held = usePrecache(isLibraryRoute(shown));

  // A sub-page starts at the top; the library comes back to where it was, so
  // returning from an article keeps your place.
  const main = useRef<HTMLElement>(null);
  const libraryTop = useRef(0);
  const onLibrary = isLibraryRoute(shown);
  // Whether the library is still on screen, read when a scroll is handled
  // rather than when the listener was attached. Leaving the library shortens
  // the content, and the browser's clamping scroll fires before this effect's
  // cleanup removes the listener. That scroll must not overwrite the saved
  // position.
  const showingLibrary = useRef(onLibrary);
  showingLibrary.current = onLibrary;
  // Whether the pane holds the reading. Not the route alone, since a deleted
  // item's page shows the library while it redirects.
  const reading = shown.name === "read";

  // The library opens on the list it was last narrowed to in this browser.
  const shownList = onLibrary ? routeListId(shown) : undefined;
  useEffect(() => {
    if (shownList !== undefined) {
      rememberList(shownList);
    }
  }, [shownList]);
  useLayoutEffect(() => {
    const scroller = required(main.current, "main");
    if (!onLibrary) {
      // One assignment, not a restore: later content only grows the page
      // downwards. A restore would keep resetting the pane while the height
      // changed, fighting the reader page placing it where this device left
      // off.
      scroller.scrollTop = 0;
      return undefined;
    }
    // The cards are not at full height on the render that brings the library
    // back: a stored thumbnail's address arrives a tick later, and the column
    // count is measured. `restoreScroll` keeps placing the box until its
    // height holds still, and while `restoring` is set the listener records
    // nothing, so a clamped offset can't replace the saved one. A layout
    // effect, so the library is never drawn at the top for a frame.
    const restore = restoreScroll(scroller, libraryTop.current);
    const onScroll = (): void => {
      if (!showingLibrary.current || restore.restoring) {
        return;
      }
      libraryTop.current = scroller.scrollTop;
    };
    scroller.addEventListener("scroll", onScroll);
    return () => {
      restore.stop();
      scroller.removeEventListener("scroll", onScroll);
    };
  }, [route, onLibrary]);

  // Whether the reading screen's bar is hidden. The state is kept in the shell
  // rather than the reader page, because the <header> that hides is here.
  const [retracted, setRetracted] = useState(false);
  // Whether the reading is within `TITLE_PAST_TOP` (see `PageHeader`).
  const [atTop, setAtTop] = useState(true);
  // The overlay bar's measured height, which the pane keeps as top padding
  // (see the `data-under-bar` rule above). jsdom measures zero.
  const bar = useRef<HTMLElement>(null);
  useEffect(() => {
    const scroller = required(main.current, "main");
    if (!reading) {
      scroller.style.removeProperty("--toucan-bar-height");
      return;
    }
    const el = required(bar.current, "header");
    scroller.style.setProperty(
      "--toucan-bar-height",
      `${el.offsetHeight.toString()}px`,
    );
  }, [reading, route]);
  useEffect(() => {
    if (!reading) {
      setRetracted(false);
      return undefined;
    }
    const scroller = required(main.current, "main");
    setAtTop(scroller.scrollTop <= TITLE_PAST_TOP);
    // The scroll direction, and the furthest point reached that way. The
    // direction flips once the scroll comes back `SHAKE` from there, and the
    // turning point becomes the new mark. It starts as going up, matching the
    // bar shown on opening.
    let goingDown = false;
    let furthest = scroller.scrollTop;
    const onScroll = (): void => {
      const top = scroller.scrollTop;
      // Restoring where this device left off is not the reader scrolling: the
      // bar stays as it opened, and the restored position is the mark the
      // reader's first scroll is measured from.
      if (beingPlaced()) {
        furthest = top;
        return;
      }
      if (goingDown ? top > furthest : top < furthest) {
        furthest = top;
      } else if (Math.abs(top - furthest) > SHAKE) {
        goingDown = !goingDown;
        furthest = top;
      }
      // The bar comes back at the `.finish` rule that ends the reading, not at
      // the bottom of the pane, so the actions under it have a header. Looked
      // up per event, since a paper grows a page at a time; jsdom lays nothing
      // out, so tests supply offsetTop.
      const finish = scroller.querySelector<HTMLElement>(".finish");
      // No `.finish` while a reading is still loading, nor just after leaving
      // it, when the pane holds the library and the browser's clamping scroll
      // lands before this listener comes off.
      if (finish === null) {
        return;
      }
      const atEnd = top + scroller.clientHeight >= finish.offsetTop;
      setRetracted(!atEnd && goingDown && top > PAST_TOP);
      setAtTop(top <= TITLE_PAST_TOP);
    };
    // A mouse held near the top edge is the way the bar comes back without
    // giving up any of the place just scrolled to.
    const onMove = (event: PointerEvent): void => {
      if (event.clientY <= NEAR_TOP) {
        setRetracted(false);
      }
    };
    scroller.addEventListener("scroll", onScroll);
    window.addEventListener("pointermove", onMove);
    return () => {
      scroller.removeEventListener("scroll", onScroll);
      window.removeEventListener("pointermove", onMove);
    };
  }, [reading, route]);

  // ⌘/Ctrl+Enter leaves a sub-page from inside the field being typed in. On a
  // page that submits a batch it presses that page's Save instead.
  const saves = SAVES[shown.name];
  useDoneKey(!isLibraryRoute(shown), () => {
    if (saves === undefined) {
      go(leavePath());
      return;
    }
    required(
      document.querySelector<HTMLFormElement>(`#${saves}`),
      "the page's form",
    ).requestSubmit();
  });

  // Escape leaves a sub-page, the same as tapping the back arrow — unless a
  // dialog is open, which already handles Escape itself.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (
        event.key === "Escape" &&
        !isLibraryRoute(shownRoute(route, stale)) &&
        document.querySelector("dialog[open]") === null
      ) {
        event.preventDefault();
        go(leavePath());
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [route, stale]);

  // The tab's name. Run on every render rather than on the route,
  // since a title can be made of library data — an item's title, a list's —
  // and renaming either has to reach the tab as well as the page.
  useEffect(() => {
    document.title = pageTitle(shown);
  });

  /** The library, narrowed to whichever of its three routes is showing. */
  const library = (): JSX.Element => (
    <LibraryPage
      listId={routeListId(shown)}
      sort={sort}
      query={query}
      held={held}
      onSort={(picked) => {
        setSort(picked);
        rememberSort(picked);
      }}
      onQuery={setQuery}
    />
  );
  const pages: Record<Route["name"], () => JSX.Element> = {
    library,
    list: library,
    unfiled: library,
    edit: () => <EditPage item={required(item, "item")} />,
    read: () => <ReadPage item={required(item, "item")} />,
    add: () => <AddPage />,
    import: () => <ImportPage />,
    lists: () => <ListsPage />,
    settings: () => (
      <ToucanSettings app={app()}>
        {!isStandaloneApp() && (
          <SaveFromBrowser reads={`${location.origin}${location.pathname}`} />
        )}
      </ToucanSettings>
    ),
  };

  return (
    <>
      <header ref={bar} data-floating={reading} data-retracted={retracted}>
        {isLibraryRoute(shown) ? (
          <BrandHeader />
        ) : (
          <PageHeader
            route={shown}
            item={item}
            atTop={atTop}
            retracted={retracted}
          />
        )}
      </header>
      <main ref={main} data-under-bar={reading}>
        {pages[shown.name]()}
      </main>
    </>
  );
}
